import React, { useMemo, useEffect, useState } from "react";
import { Grid, TextField, Button, Box, Select, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import { GET_CSE_LIST } from "../../constants";
import axios from "axios";

const CallFilterComponent = ({
  filters,
  setFilters,
  data,
  applyFilters,
  setFilterShow,
}) => {
  const today = dayjs().startOf("day");

  // Predefined date ranges
  const predefinedRanges = [
    { label: "Today", startDate: today, endDate: today },
    {
      label: "Yesterday",
      startDate: today.subtract(1, "day"),
      endDate: today.subtract(1, "day"),
    },
    {
      label: "Last 7 Days",
      startDate: today.subtract(6, "day"),
      endDate: today,
    },
    {
      label: "Last Month",
      startDate: today.subtract(1, "month").startOf("month"),
      endDate: today.subtract(1, "month").endOf("month"),
    },
    { label: "Custom", startDate: null, endDate: null },
  ];

  const [filtersData, setFiltersData] = useState({
    selectedRange: predefinedRanges[3], // Default to "Last 7 Days"
    customStartDate: predefinedRanges[3].startDate,
    customEndDate: predefinedRanges[3].endDate,
    agentName: "",
  });

  useEffect(() => {
    // Load filters from localStorage on page load
    const savedFilters = localStorage.getItem("callfilters");
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);

      // Ensure startDate and endDate are valid and set as dayjs objects
      if (filters.startDate && filters.endDate) {
        filters.customStartDate = dayjs(filters.startDate);
        filters.customEndDate = dayjs(filters.endDate);
      }

      // If startDate or endDate are null or invalid, use a default range
      else {
        filters.customStartDate = dayjs().subtract(7, "days"); // Default to 7 days ago
        filters.customEndDate = dayjs(); // Default to today
      }

      // Check if saved filters match any predefined range
      const matchedRange = predefinedRanges.find(
        (range) =>
          range.startDate &&
          range.endDate &&
          range.startDate.isSame(filters.customStartDate, "day") &&
          range.endDate.isSame(filters.customEndDate, "day")
      );

      // Set the selected range to "Custom" if no match found
      setFiltersData({
        ...filters,
        selectedRange:
          matchedRange ||
          predefinedRanges.find((range) => range.label === "Custom"),
      });
    }
  }, []); // This runs only once on initial load

  const [cseList, setCseList] = useState([]);
  const fetchCseList = async () => {
    if (cseList.length === 0) {
      // Only fetch if options are not already loaded

      try {
        const response = await axios.get(GET_CSE_LIST);
        setCseList(response.data);
      } catch (error) {
        console.error("Error fetching inquiry types:", error);
      }
    }
  };

  useEffect(() => {
    fetchCseList();
  }, []);

  const handleInputChange = (key, value) => {
    setFiltersData((prevFilters) => {
      const updatedFilters = { ...prevFilters, [key]: value };

      // When the selected range is changed, update the start and end dates
      if (key === "selectedRange") {
        if (value.label !== "Custom") {
          updatedFilters.customStartDate = value.startDate;
          updatedFilters.customEndDate = value.endDate;
        } else {
          // Keep custom dates intact for "Custom" range
        }
      }
      return updatedFilters;
    });
  };

  const handleCustomDateChange = (key, value) => {
    setFiltersData((prevFilters) => ({
      ...prevFilters,
      [key]: dayjs(value),
    }));
  };

  const handleApply = () => {
    const { selectedRange, customStartDate, customEndDate } = filtersData;

    const startDate =
      selectedRange.label === "Custom"
        ? customStartDate && customStartDate.isValid()
          ? customStartDate.format("YYYY-MM-DD")
          : null
        : selectedRange.startDate
        ? selectedRange.startDate.format("YYYY-MM-DD")
        : null;

    const endDate =
      selectedRange.label === "Custom"
        ? customEndDate && customEndDate.isValid()
          ? customEndDate.format("YYYY-MM-DD")
          : null
        : selectedRange.endDate
        ? selectedRange.endDate.format("YYYY-MM-DD")
        : null;

    setFilters({
      startDate,
      endDate,
      agentName: filtersData.agentName,
    });

    // Persist filters in localStorage
    localStorage.setItem(
      "callfilters",
      JSON.stringify({ startDate, endDate, agentName: filtersData.agentName })
    );

    setFilterShow(false);
  };

  const handleClear = () => {
    const defaultRange = predefinedRanges[2]; // "Last 7 Days"
    setFilters({
      startDate: defaultRange.startDate.format("YYYY-MM-DD"),
      endDate: defaultRange.endDate.format("YYYY-MM-DD"),
      agentName: "",
    });

    setFiltersData({
      selectedRange: defaultRange,
      customStartDate: defaultRange.startDate,
      customEndDate: defaultRange.endDate,
      agentName: "",
    });

    // Remove filters from localStorage
    localStorage.removeItem("callfilters");

    setFilterShow(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f0f4f8",
        padding: 1,
        borderRadius: 2,
        boxShadow: 3,
        width: "100%",
      }}
    >
      <Grid container spacing={2}>
        {/* Predefined Ranges */}
        <Grid item xs={12} sm={3}>
          <Select
            value={filtersData.selectedRange.label}
            onChange={(e) =>
              handleInputChange(
                "selectedRange",
                predefinedRanges.find((range) => range.label === e.target.value)
              )
            }
            fullWidth
            size="small"
          >
            {predefinedRanges.map((range) => (
              <MenuItem key={range.label} value={range.label}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Date Pickers */}
        <Grid item xs={12} sm={3}>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={
              filtersData.customStartDate
                ? filtersData.customStartDate.format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              handleCustomDateChange("customStartDate", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="End Date"
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={
              filtersData.customEndDate
                ? filtersData.customEndDate.format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              handleCustomDateChange("customEndDate", e.target.value)
            }
          />
        </Grid>

        {/* Agent Name Autocomplete */}
        {/* <Grid item xs={12} sm={2}>
          <Autocomplete
            options={cseList}
            value={
              cseList.find(
                (option) => option.EmployeeName === filtersData.agentName
              ) || null
            }
            onChange={(event, newValue) =>
              handleInputChange("agentName", newValue?.EmployeeName || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agent Name"
                size="small"
                fullWidth
              />
            )}
            getOptionLabel={(option) => option.EmployeeName || ""}
            isOptionEqualToValue={(option, value) =>
              option.EmployeeName === value
            }
          />
        </Grid> */}

        {/* Action Buttons */}
        <Grid item xs={12} sm={3}>
          <Box sx={{ display: "flex" }}>
            <Button
              onClick={handleClear}
              variant="outlined"
              color="secondary"
              sx={{ marginRight: 2 }}
            >
              Clear
            </Button>
            <Button onClick={handleApply} variant="contained" color="primary">
              Apply
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CallFilterComponent;
